import type { settingTypes } from '@ipfabric/utils';
import type { ExistingVendor } from '../vendorTypes';

export type ResponseVendor = {
  id: string;
  type: settingTypes.VendorApiType;
  slug?: string;
};

/**
 * Replace each local vendor's `id` with the BE-assigned `_key` returned in the
 * PATCH /settings response, and clear the `unsaved` flag on replaced entries.
 *
 * This exists to defeat the silent-credential-wipe bug: the BE recreate loop
 * in `patch-settings.ts` preserves encrypted fields (apiToken, apiKey,
 * password, ...) only when the request's `id` equals the DB `_key`. Without
 * this sync, a FE-generated guid lingers across same-session edits and the
 * BE-side lookup fails, causing the BE to write a credential-less row on the
 * next save.
 *
 * Matching strategy: try `slug + type` first (stable identity used by the BE
 * for duplicate detection); fall back to positional match within `type` for
 * vendors with no slug (the BE recreate at `patch-settings.ts:174` uses
 * `Bluebird.mapSeries` which preserves request order). Each response entry is
 * consumed at most once, so slug-matched entries don't also count toward the
 * positional fallback.
 */
export function syncVendorIdsFromResponse(
  currentVendors: ExistingVendor[],
  responseVendors: ResponseVendor[],
): ExistingVendor[] {
  const remainingByType = new Map<
    settingTypes.VendorApiType,
    ResponseVendor[]
  >();
  for (const vendor of responseVendors) {
    const bucket = remainingByType.get(vendor.type) ?? [];
    bucket.push(vendor);
    remainingByType.set(vendor.type, bucket);
  }

  return currentVendors.map((vendor) => {
    const bucket = remainingByType.get(vendor.type);
    if (!bucket || bucket.length === 0) {
      return vendor;
    }

    let matchIndex = -1;
    if (vendor.slug) {
      matchIndex = bucket.findIndex((r) => r.slug === vendor.slug);
    }
    if (matchIndex === -1) {
      matchIndex = 0;
    }
    const match = bucket[matchIndex];
    if (!match) {
      return vendor;
    }
    bucket.splice(matchIndex, 1);

    if (match.id === vendor.id && !vendor.unsaved) {
      return vendor;
    }
    const { unsaved: _unsaved, ...rest } = vendor;
    return { ...rest, id: match.id } as ExistingVendor;
  });
}
